/** @format */

import React, { useEffect } from "react";
import { Lesson } from "../resources/types";
import { useParams, useNavigate } from "react-router-dom";
import { useLocale } from "../i18n/hooks";
import { useTranslation } from "react-i18next";

import "./LessonPage.scss";
import { ReactComponent as ArrowLeftCircleFill } from "bootstrap-icons/icons/arrow-left-circle-fill.svg";
import { ReactComponent as ArrowRightCircleFill } from "bootstrap-icons/icons/arrow-right-circle-fill.svg";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  lessonProgress,
  selectLessonsProgress,
  updateLessonProgress,
} from "../lessonProgress/processSlice";
import { produce } from "immer";
import { Button } from "react-bootstrap";

import AddEdit from "../contentEditor/addEdit";
import { UuidString } from "../userResources/userResourceFunctions";
import ResourceCards from "../card/ResourceCards";
import UserResourceCards from "../card/UserResourceCards";
import LevelBanners from "../levelBanners/LevelBanners";
import PageNotFound from "../pageNotFound/PageNotFound";
import FormattedText from "../formattedText/FormattedText";

type Props = {
  lesson?: Lesson;
  resourceUUID: UuidString;
  resourceId?: string;
};

const LessonPageUI: React.FC<Props> = ({
  lesson,
  resourceUUID,
  resourceId,
}) => {
  const locale = useLocale();
  const { t: _ } = useTranslation();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const { lessonId, pageId, userId } = useParams();

  var pageIdNumber = Number(pageId);

  //TODO: Fix lesson progress bug
  const lessonsProgress = useAppSelector(selectLessonsProgress());
  const progress: Number =
    lessonsProgress.find(
      (lessonProgress: lessonProgress) =>
        lessonProgress.lessonUUID === lesson?.uuid &&
        resourceUUID === lessonProgress.resourceUUID,
    )?.progress ?? 0;
  const progressPercentage = lesson?.subLessons.length
    ? ((Number(progress) + 1) / lesson?.subLessons.length) * 100
    : 0;

  let lessonTitle;
  let lessonImage;
  let lessonImagePosition;
  let lessonImageAltText;
  let subLessonTitle;

  const sublesson = lesson?.subLessons?.[pageIdNumber];

  if (lesson) {
    lessonTitle =
      typeof lesson.title === "object" ? lesson.title?.[locale] : lesson?.title;
    lessonImage =
      typeof lesson.heroImage === "object"
        ? lesson.heroImage.src
        : lesson.heroImage;
    lessonImageAltText =
      typeof lesson.heroImage === "object"
        ? lesson.heroImage.$metadata?.alt
        : undefined;
    lessonImagePosition =
      typeof lesson.heroImage === "object"
        ? lesson.heroImage.$metadata?.positionY
        : undefined;
  }

  if (sublesson) {
    subLessonTitle =
      typeof sublesson.title === "object"
        ? sublesson?.title[locale]
        : sublesson?.title;
  }

  useEffect(() => {
    if (lesson && sublesson) {
      dispatch(
        updateLessonProgress(
          produce(lessonsProgress, (draft) => {
            const lessonProgress = draft.find(
              (lessonProgress: lessonProgress) =>
                lessonProgress.lessonUUID === lesson.uuid &&
                resourceUUID === lessonProgress.resourceUUID,
            );
            if (lessonProgress) lessonProgress.progress = pageIdNumber;
            else
              draft.push({
                lessonUUID: lesson.uuid as UuidString,
                progress: pageIdNumber,
                resourceUUID: resourceUUID as UuidString,
              });
          }),
        ),
      );
    }
  }, [
    dispatch,
    lesson,
    pageIdNumber,
    lessonsProgress,
    resourceUUID,
    sublesson,
  ]);

  const handlePrevClick = () => {
    pageIdNumber--;
    return navigate("../" + lessonId + "/" + pageIdNumber);
  };

  const handleNextClick = () => {
    pageIdNumber++;
    return navigate("../" + lessonId + "/" + pageIdNumber);
  };

  return (
    <>
      {lesson && resourceId ? (
        <div className="lesson-page">
          <div className="lesson-breadcrumb p-3 rounded">
            <FormattedText
              html={`${lessonTitle} / ${subLessonTitle ?? "Not Found"}`}
            />
          </div>

          {sublesson ? (
            <>
              <div className={`progress progress-warning`}>
                <div
                  className={`progress-bar bg-warning`}
                  role="progressbar"
                  aria-label="progress warning"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={progressPercentage}
                  style={{ width: `${progressPercentage}%` }}
                ></div>
              </div>

              <div className="card">
                <AddEdit
                  action="lesson"
                  stage={2}
                  itemId={lesson.uuid}
                  borderOffset="no-offset"
                >
                  <div
                    className="card-header sublesson-header text-center text-white d-flex flex-column flex-lg-row align-items-center justify-content-between p-0"
                    role="img"
                    title={lessonImageAltText}
                    style={{
                      backgroundPositionY: `${lessonImagePosition ?? 50}%`,
                      backgroundImage: `url("${lessonImage}")`,
                    }}
                  >
                    <div className="card-title flex-grow-1 w-auto mx-5">
                      <h5 className="lesson-title my-5 mx-auto py-4 px-5">
                        <FormattedText html={subLessonTitle ?? ""} />
                      </h5>
                    </div>
                    <LevelBanners levels={lesson.difficultyLevels} />
                  </div>
                </AddEdit>

                <div className="card-body px-2 px-sm-3">
                  <AddEdit action="card" className="maxWidth" itemId="0" />
                  {userId && sublesson?.originalUUID !== undefined ? (
                    <UserResourceCards
                      pageId={pageId}
                      resourceId={resourceId}
                      sublesson={sublesson}
                    />
                  ) : (
                    <ResourceCards
                      sublesson={sublesson}
                      resourceId={resourceId}
                      subLessonUuid={sublesson?.uuid as UuidString}
                      pageId={pageId ?? ""}
                    />
                  )}
                </div>
                <div className="card-footer py-4 px-5 text-end">
                  <div className="btn-group">
                    {pageIdNumber === 0 && (
                      <Button
                        variant="outline-primary"
                        onClick={() => {
                          navigate("/" + locale + "/" + resourceId);
                        }}
                      >
                        <ArrowLeftCircleFill
                          style={{ verticalAlign: "-0.125em" }}
                        />{" "}
                        {_("Dashboard")}
                      </Button>
                    )}
                    {pageIdNumber > 0 && (
                      <Button
                        variant="outline-primary"
                        onClick={() => {
                          handlePrevClick();
                        }}
                      >
                        <ArrowLeftCircleFill
                          style={{ verticalAlign: "-0.125em" }}
                        />{" "}
                        {_("Previous")}
                      </Button>
                    )}

                    {pageIdNumber + 1 !== lesson?.subLessons.length && (
                      <Button
                        variant="primary"
                        onClick={() => {
                          handleNextClick();
                        }}
                      >
                        {_("Next")}{" "}
                        <ArrowRightCircleFill
                          style={{ verticalAlign: "-0.125em" }}
                        />
                      </Button>
                    )}

                    {pageIdNumber + 1 === lesson?.subLessons.length && (
                      <Button
                        variant="success"
                        onClick={() => {
                          navigate("/" + locale + "/" + resourceId);
                        }}
                      >
                        {_("Complete")}{" "}
                        <ArrowRightCircleFill
                          style={{ verticalAlign: "-0.125em" }}
                        />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <PageNotFound itemName={"Sublesson"} />
          )}
        </div>
      ) : (
        <PageNotFound itemName={"Lesson"} />
      )}
    </>
  );
};

export default LessonPageUI;
